import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import distanceIcon from "../../../img/svg/carousel_location_icon.svg";
import searchIcon from "../../../img/svg/search_icon2.svg";
import Dropdown from "../../../shared/Dropdown";
import ImageWithFallback from "../../../shared/imageWithFallback";
import { PoweredBy } from "../../../shared/PoweredBy";
import { V2_CATEGORIES } from "../../data/categories";
import ScreenHeader from "../../components/ScreenHeader";
import useUserLocation from "../../hooks/useUserLocation";
import { distanceInMeters, formatDistance } from "../../utils/geo";
import useSubCategoryClubs from "./useSubCategoryClubs";
import { SHOW_ACCESS_TYPE_BADGES } from "../../../testSimulation";
import "./fitExtendSelector.scss";

// QA aid: badge showing each club's access type on the list, toggled by
// SHOW_ACCESS_TYPE_BADGES in src/testSimulation.js (local dev server only).
// Mirrors the exact flag cascade of chooseTextForBtn/handleChooseRightFlow in src/screens/venue.jsx.
const showAccessBadge = import.meta.env.DEV && SHOW_ACCESS_TYPE_BADGES;

const getAccessType = (club) => {
  const isGeneralCondition =
    club.IsStudioRbox || club.IsStudioFizikal || club.IsStudioLeap || club.IsStudioBoostapp || club.IsStudioFeatShare;

  if ((club.IsStudio || club.IsClassSchedule) && isGeneralCondition) return "calendar";
  if (club.IsVenueClub && isGeneralCondition) return "phone";
  if (club.IsStudio) return "phone";
  if (club.IsStudioRbox && club.IsStudioFizikal) return "calendar";
  if (club.IsStudioFeatShare) return "calendar";
  if (club.IsStudioFizikal || club.IsStudioLeap || club.IsStudioBoostapp) return "phone";
  if (club.IsStudioRbox) return "calendar";
  return "checkin";
};

const ACCESS_TYPE_LABELS = { checkin: "Check-in", calendar: "Calendar", phone: "Phone" };

const AccessBadge = ({ club }) => {
  const accessType = getAccessType(club);
  return (
    <span className={`fes-v2-row-badge fes-v2-row-badge--${accessType}`}>{ACCESS_TYPE_LABELS[accessType]}</span>
  );
};

// Path data from img/svg/location.svg, inlined with currentColor so the pill's
// CSS colours it in the partner's brand colour (the asset itself is hardcoded red).
const LocationPinIcon = () => (
  <svg width="20" height="20" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M40.0002 6.66699C32.9277 6.66699 26.145 9.47651 21.144 14.4775C16.143 19.4784 13.3335 26.2612 13.3335 33.3337C13.3335 51.3337 36.8335 71.667 37.8335 72.5337C38.4373 73.0501 39.2057 73.3339 40.0002 73.3339C40.7947 73.3339 41.5631 73.0501 42.1668 72.5337C43.3335 71.667 66.6668 51.3337 66.6668 33.3337C66.6668 26.2612 63.8573 19.4784 58.8563 14.4775C53.8554 9.47651 47.0726 6.66699 40.0002 6.66699ZM40.0002 65.5003C32.9002 58.8337 20.0002 44.467 20.0002 33.3337C20.0002 28.0293 22.1073 22.9423 25.858 19.1915C29.6088 15.4408 34.6958 13.3337 40.0002 13.3337C45.3045 13.3337 50.3916 15.4408 54.1423 19.1915C57.893 22.9423 60.0002 28.0293 60.0002 33.3337C60.0002 44.467 47.1002 58.867 40.0002 65.5003ZM40.0002 20.0003C37.3631 20.0003 34.7852 20.7823 32.5926 22.2474C30.3999 23.7125 28.6909 25.7949 27.6818 28.2312C26.6726 30.6676 26.4086 33.3484 26.923 35.9349C27.4375 38.5213 28.7074 40.8971 30.5721 42.7618C32.4368 44.6265 34.8125 45.8963 37.399 46.4108C39.9854 46.9253 42.6663 46.6612 45.1026 45.6521C47.539 44.6429 49.6213 42.9339 51.0864 40.7413C52.5515 38.5486 53.3335 35.9707 53.3335 33.3337C53.3335 29.7974 51.9287 26.4061 49.4283 23.9056C46.9278 21.4051 43.5364 20.0003 40.0002 20.0003ZM40.0002 40.0003C38.6816 40.0003 37.3927 39.6093 36.2964 38.8768C35.2 38.1442 34.3456 37.1031 33.841 35.8849C33.3364 34.6667 33.2044 33.3263 33.4616 32.0331C33.7188 30.7399 34.3538 29.552 35.2861 28.6196C36.2185 27.6873 37.4064 27.0523 38.6996 26.7951C39.9928 26.5379 41.3332 26.6699 42.5514 27.1745C43.7696 27.679 44.8108 28.5335 45.5433 29.6299C46.2758 30.7262 46.6668 32.0151 46.6668 33.3337C46.6668 35.1018 45.9645 36.7975 44.7142 38.0477C43.464 39.2979 41.7683 40.0003 40.0002 40.0003Z"
      fill="currentColor"
    />
  </svg>
);

function FitExtendSelector({ clubs, cityOptions, onCitySelect, isOrthodox, defaultImg, imgFallbackSrc, showPoweredBy }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");

  // The category filter lives entirely in V2: the chips come from a static list
  // and resolve through the name-based endpoint, so the V1 screen's ID-based
  // category state is never involved. Seeded once from the home page's tiles.
  const [selectedCategory, setSelectedCategory] = useState(location.state?.categoryName ?? "");
  const { status: categoryStatus, allowedClubIds } = useSubCategoryClubs(selectedCategory);

  // Keyed by label rather than index: the "All activities" chip sits before the
  // mapped list, so V2_CATEGORIES indexes are off by one from DOM order.
  const chipRefs = useRef({});
  // Entry-time only (deliberately not re-run on selection): a chip tapped
  // inside this screen is already visible, and the strip must not move under
  // the user's finger. scrollIntoView keeps this RTL-safe — no scrollLeft math.
  useEffect(() => {
    if (!selectedCategory) return;
    chipRefs.current[selectedCategory]?.scrollIntoView({
      behavior: "auto",
      inline: "center",
      block: "nearest",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // A category is selected but its clubs aren't known yet. Rendering the
  // unfiltered list here would flash every club before narrowing to the filter.
  const categoryPending = categoryStatus === "loading";

  // The rows carry the same distance the home carousels do, so they resolve the
  // user the same way: browser geolocation, then the saved client address. The
  // hook belongs here and not in the V1 wrapper, whose hooks run for every
  // partner — V2 is Clalit-only, and no other partner should be asked for a
  // location it never shows.
  const { coords } = useUserLocation();

  // RecordID -> metres, built off the *unfiltered* list so typing in the search
  // box never recomputes hundreds of haversines. Clubs the API has no usable
  // coordinates for are simply absent from the map, which reads as "unknown"
  // everywhere below.
  const distanceByClubId = useMemo(() => {
    const map = new Map();
    if (!coords || !Array.isArray(clubs)) return map;
    clubs.forEach((el) => {
      // Same PascalCase fields GetCarousels returns.
      const meters = distanceInMeters(coords, { latitude: el.latitude, longitude: el.longitude });
      if (meters != null) map.set(el.RecordID, meters);
    });
    return map;
  }, [clubs, coords]);

  const filteredClubs = useMemo(() => {
    if (categoryPending) return [];
    let list = Array.isArray(clubs) ? clubs : [];
    if (allowedClubIds) list = list.filter((el) => allowedClubIds.has(el.RecordID));
    if (searchQuery) list = list.filter((el) => el.Name?.toLowerCase().includes(searchQuery.toLowerCase()));
    // Nearest first, clubs with no known distance last. Skipped outright when
    // the location is unknown, so the API's own ordering stays visible. Sorted
    // on a copy: with no filter applied `list` is still the `clubs` prop, and
    // that is appParamSlice's ClubList, which venue and home read too.
    // Array#sort is stable, so ties keep the API order.
    if (!coords) return list;
    return [...list].sort((a, b) => {
      const aMeters = distanceByClubId.get(a.RecordID);
      const bMeters = distanceByClubId.get(b.RecordID);
      if (aMeters == null) return bMeters == null ? 0 : 1;
      if (bMeters == null) return -1;
      return aMeters - bMeters;
    });
  }, [clubs, allowedClubIds, searchQuery, categoryPending, coords, distanceByClubId]);

  return (
    <div className="fes-v2">
      <ScreenHeader title={t("WHERE_TO_WORKOUT_TODAY")} onBack={() => navigate(-1)} />
      <div className="fes-v2-filters">
        <div className="fes-v2-search-pill">
          <img className="fes-v2-pill-icon" src={searchIcon} alt="" />
          <input
            type="text"
            className="fes-v2-search-input"
            placeholder={t("SEARCH")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="fes-v2-city-pill">
          <Dropdown options={cityOptions} onSelect={onCitySelect} icon={<LocationPinIcon />} placeholder={t("SELECT_SEARCH_CITY")} />
        </div>
      </div>

      <div className="fes-v2-chips">
        <button
          type="button"
          className={`fes-v2-chip${selectedCategory === "" ? " fes-v2-chip--selected" : ""}`}
          onClick={() => setSelectedCategory("")}
        >
          {t("ALL_ACTIVITIES")}
        </button>
        {V2_CATEGORIES.map((category) => (
          <button
            type="button"
            key={category.key}
            ref={(el) => {
              chipRefs.current[category.label] = el;
            }}
            className={`fes-v2-chip${selectedCategory === category.label ? " fes-v2-chip--selected" : ""}`}
            onClick={() => setSelectedCategory(category.label)}
          >
            {category.label}
          </button>
        ))}
      </div>

      {/* Hidden while pending — "0 תוצאות" under a spinner reads as "nothing found". */}
      {!categoryPending && (
        <p className="fes-v2-results-count">
          {filteredClubs.length} {t("RESULTS")}
        </p>
      )}

      <div className="fes-v2-list">
        {filteredClubs.map((el) => {
          // null when the user's location is unknown or this club has no
          // coordinates, in which case the line is dropped rather than left
          // blank — flex `gap` adds no space for an absent child.
          const distanceLabel = formatDistance(distanceByClubId.get(el.RecordID), t);
          return (
            <div
              className="fes-v2-row"
              key={el.RecordID}
              onClick={() => {
                navigate(`/venue/${el.RecordID}`, {
                  state: {
                    price: el.Price,
                    clubId: el.RecordID,
                    clubName: el.Name,
                  },
                });
              }}
            >
              <div className="fes-v2-row-img">
                <ImageWithFallback src={isOrthodox ? defaultImg : el.MobClubLogoPath} fallbackSrc={imgFallbackSrc} alt="Club logo" />
              </div>
              <div className="fes-v2-row-text">
                <p className="fes-v2-row-name">
                  {el.Name}
                  {showAccessBadge && <AccessBadge club={el} />}
                </p>
                <p className="fes-v2-row-address">{el.Address}</p>
                {distanceLabel && (
                  <p className="fes-v2-row-distance">
                    <img src={distanceIcon} alt="" className="fes-v2-row-distance-icon" />
                    {distanceLabel}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* {showPoweredBy && (
        <div className="fes-v2-powered-by">
          <PoweredBy />
        </div>
      )} */}
    </div>
  );
}

export default FitExtendSelector;
